import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Skeleton } from '@mantine/core';

import { FilesSearchWidget } from 'types/dashboard_widget';
import { TrackableItem, TrackableList } from 'types/trackables';

import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { useCameras } from 'cameras/hooks/useCameras';
import { useDashboard } from 'dashboards/hooks/useDashboard';
import { useGateways } from 'gateways/hooks/useGateways';
import { useModal } from 'hooks/useModal';
import { useStreams } from 'streams/hooks/useStreams';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';

import { Accordion } from 'components/Accordion/Accordion';
import { CameraSelect } from 'cameras/components/CameraSelect/CameraSelect';
import { Field } from 'components/Field/Field';
import { FilesSearchConfidenceSlider } from 'files/components/FilesSearchConfidenceSlider/FilesSearchConfidenceSlider';
import { FilesSearchEntitiesInput } from 'files/components/FilesSearchEntitiesInput/FilesSearchEntitiesInput';
import { GatewaySelect } from 'gateways/components/GatewaySelect/GatewaySelect';
import { Input } from 'components/Input/Input';
import { OptionalTooltip } from 'components/Tooltip/OptionalTooltip';
import { Radio, RadioOption } from 'components/Radio/Radio';
import { StreamSelect } from 'streams/components/StreamSelect/StreamSelect';
import { TrackableItemSelect } from 'trackables/components/TrackableItemSelect/TrackableItemSelect';
import { TrackableListSelect } from 'trackables/components/TrackableListSelect/TrackableListSelect';
import { useTrackableLists, useTrackableNames } from 'trackables/hooks';

import styles from './FilesSearchForm.module.scss';
import { WidgetFormHeader } from './Header';
import { WidgetFormFooter } from './Footer';

type FilesSearchFormFieldValues = FilesSearchWidget['options'] & {
  name: string;
};

type FilesSearchForm = { widget?: FilesSearchWidget; dashboardId: string };

export function FilesSearchForm({ widget, dashboardId }: FilesSearchForm) {
  const { close } = useModal();

  const { data: dashboard } = useDashboard(dashboardId);

  const options: FilesSearchWidget['options'] = widget
    ? widget.options
    : {
        query: '',
        search_entities: [
          'events',
          'objects',
          'texts',
          'trackables',
          'vectors',
        ],
        confidence: 0.5,
        sort_by: 'relevance',
        camera_ids: [],
        stream_ids: [],
        gateway_ids: [],
        trackable_list_ids: [],
        trackable_item_ids: [],
      };

  const defaultValues = React.useMemo(() => {
    const base = {
      ...options,
      trackable_list_ids: options.trackable_list_ids ?? [],
      trackable_item_ids: options.trackable_item_ids ?? [],
    };

    return widget ? { ...base, name: widget.name } : { ...base, name: '' };
  }, [options, widget]);

  const {
    control,
    formState: { dirtyFields, errors },
    handleSubmit,
    register,
    watch,
    setValue,
    clearErrors,
  } = useForm<FilesSearchFormFieldValues>({
    defaultValues,
  });

  const isDirty = Boolean(Object.keys(dirtyFields).length);

  const { mutate: updateDashboard, isLoading: isUpdating } = useUpdateDashboard(
    {
      onSuccess: () => close(),
    }
  );

  const hasDefaultCameras = options.camera_ids.length > 0;
  const hasDefaultStreams = options.stream_ids.length > 0;
  const hasDefaultGateways = options.gateway_ids.length > 0;
  const hasDefaultTrackableLists =
    options.trackable_list_ids && options.trackable_list_ids.length > 0;
  const hasDefaultTrackableItems =
    options.trackable_item_ids && options.trackable_item_ids.length > 0;

  const { data: cameras, isInitialLoading: isCamerasLoading } = useCameras({
    params: {
      camera_ids: options.camera_ids,
      limit: options.camera_ids.length,
    },
    enabled: hasDefaultCameras,
  });

  const { data: streams, isInitialLoading: isStreamsLoading } = useStreams({
    params: {
      stream_ids: options.stream_ids,
      limit: options.stream_ids.length,
    },
    enabled: hasDefaultStreams,
  });

  const { data: gateways, isInitialLoading: isGatewaysLoading } = useGateways({
    params: {
      gateway_ids: options.gateway_ids,
      limit: options.gateway_ids.length,
    },
    enabled: hasDefaultGateways,
  });

  const { data: trackableLists, isInitialLoading: isTrackableListsLoading } =
    useTrackableLists({
      params: {
        trackable_list_ids: options.trackable_list_ids,
        limit: options.trackable_list_ids?.length ?? 0,
      },
      enabled: hasDefaultTrackableLists,
    });

  const queryValue = watch('query') ?? '';
  const trackableListIds = watch('trackable_list_ids') ?? [];
  const trackableItemIds = watch('trackable_item_ids') ?? [];
  const searchEntities = watch('search_entities') ?? [];
  const trackablesEnabled = searchEntities.includes('trackables');
  const hasActiveTrackableFilters =
    trackablesEnabled &&
    (trackableListIds.length > 0 || trackableItemIds.length > 0);
  const hasSearchText = queryValue.trim().length > 0;
  const meetsSearchRequirement = hasActiveTrackableFilters || hasSearchText;
  const isSearchTextRequired = !meetsSearchRequirement;

  const trackableNamesQuery = useTrackableNames(trackableItemIds);

  const [selectedTrackableLists, setSelectedTrackableLists] = React.useState<
    TrackableList[]
  >([]);
  const [selectedTrackableItems, setSelectedTrackableItems] = React.useState<
    TrackableItem[]
  >([]);

  const hasInitializedTrackableLists = React.useRef(false);
  const hasInitializedTrackableItems = React.useRef(false);

  React.useEffect(() => {
    if (meetsSearchRequirement) {
      clearErrors('query');
    }
  }, [clearErrors, meetsSearchRequirement]);

  React.useEffect(() => {
    if (
      hasInitializedTrackableLists.current ||
      !hasDefaultTrackableLists ||
      !trackableLists?.data?.length
    ) {
      return;
    }

    setSelectedTrackableLists(trackableLists.data);
    hasInitializedTrackableLists.current = true;
  }, [hasDefaultTrackableLists, trackableLists]);

  React.useEffect(() => {
    if (
      hasInitializedTrackableItems.current ||
      !hasDefaultTrackableItems ||
      !trackableItemIds.length ||
      !trackableNamesQuery.data
    ) {
      return;
    }

    const items = trackableItemIds
      .map((id) => trackableNamesQuery.data?.items[id])
      .filter((item): item is TrackableItem => Boolean(item));

    if (items.length) {
      setSelectedTrackableItems(items);
      hasInitializedTrackableItems.current = true;
    }
  }, [hasDefaultTrackableItems, trackableItemIds, trackableNamesQuery.data]);

  React.useEffect(() => {
    if (!trackablesEnabled) {
      if (selectedTrackableLists.length) {
        setSelectedTrackableLists([]);
      }

      if (selectedTrackableItems.length) {
        setSelectedTrackableItems([]);
      }

      if (trackableListIds.length) {
        setValue('trackable_list_ids', [], { shouldDirty: true });
      }

      if (trackableItemIds.length) {
        setValue('trackable_item_ids', [], { shouldDirty: true });
      }

      return;
    }

    if (!selectedTrackableLists.length) {
      if (selectedTrackableItems.length) {
        setSelectedTrackableItems([]);
        if (trackableItemIds.length) {
          setValue('trackable_item_ids', [], { shouldDirty: true });
        }
      }
      if (trackableListIds.length) {
        setValue('trackable_list_ids', [], { shouldDirty: true });
      }
      return;
    }

    const listIds = selectedTrackableLists.map(({ id }) => id);
    if (
      trackableListIds.length !== listIds.length ||
      trackableListIds.some((id) => !listIds.includes(id))
    ) {
      setValue('trackable_list_ids', listIds, { shouldDirty: true });
    }

    if (!selectedTrackableItems.length) {
      return;
    }

    const allowedIds = new Set(listIds);
    const filteredItems = selectedTrackableItems.filter((item) =>
      allowedIds.has(item.trackable_list_id)
    );

    if (filteredItems.length !== selectedTrackableItems.length) {
      setSelectedTrackableItems(filteredItems);
      setValue(
        'trackable_item_ids',
        filteredItems.map(({ id }) => id),
        { shouldDirty: true }
      );
    }
  }, [
    trackablesEnabled,
    selectedTrackableLists,
    selectedTrackableItems,
    setValue,
    trackableItemIds.length,
    trackableListIds,
  ]);

  function onSubmit({ name, ...options }: FilesSearchFormFieldValues) {
    if (!dashboard) {
      return;
    }

    const id = widget ? widget.id : crypto.randomUUID();
    const layout = widget
      ? widget.layout
      : getNewWidgetLayout(dashboard.configuration.widgets, 4, 5);

    const updatedDashboard = upsertWidget(
      dashboard,
      new FilesSearchWidget(id, name, options, layout)
    );

    updateDashboard({
      id: updatedDashboard.id,
      configuration: updatedDashboard.configuration,
    });
  }

  const isConfidenceSliderDisabled = !(
    searchEntities.length && searchEntities.includes('vectors')
  );

  return (
    <form className="flex h-full flex-col" onSubmit={handleSubmit(onSubmit)}>
      <WidgetFormHeader type="files-search" />
      <Field label="Widget name" error={errors.name} required>
        <Input
          {...register('name', {
            required: 'Please choose a name for this widget.',
          })}
        />
      </Field>

      <Field
        label="Search text"
        error={errors.query}
        required={isSearchTextRequired}
      >
        <Input
          {...register('query', {
            validate: (value) => {
              if (isSearchTextRequired && !value?.trim()) {
                return 'Please add a search text or select trackables.';
              }
              return true;
            },
          })}
          placeholder="ex. person"
        />
      </Field>

      <div className={styles.trackableFilters}>
        <Field
          label="Watchlists"
          className={styles.trackableField}
          labelVerticalAlign="start"
        >
          <Controller
            name="trackable_list_ids"
            control={control}
            render={({ field: { onChange } }) => {
              if (
                hasDefaultTrackableLists &&
                isTrackableListsLoading &&
                !selectedTrackableLists.length
              ) {
                return <Skeleton height="2rem" width="100%" />;
              }

              return (
                <TrackableListSelect<true>
                  size="small"
                  menuPlacement="top"
                  value={selectedTrackableLists}
                  onChange={(lists) => {
                    const selections = Array.isArray(lists) ? lists : [];
                    setSelectedTrackableLists(selections);
                    onChange(selections.map(({ id }) => id));
                  }}
                  isMulti
                  isClearable
                  isDisabled={!trackablesEnabled}
                />
              );
            }}
          />
        </Field>

        <Field
          label="Watchlist item"
          className={styles.trackableField}
          labelVerticalAlign="start"
        >
          <Controller
            name="trackable_item_ids"
            control={control}
            render={({ field: { onChange } }) => {
              if (
                hasDefaultTrackableItems &&
                trackableNamesQuery.isInitialLoading &&
                !selectedTrackableItems.length
              ) {
                return <Skeleton height="2rem" width="100%" />;
              }

              return (
                <TrackableItemSelect<true>
                  size="small"
                  trackableLists={selectedTrackableLists}
                  value={selectedTrackableItems}
                  onChange={(items) => {
                    const selections = Array.isArray(items) ? items : [];
                    setSelectedTrackableItems(selections);
                    onChange(selections.map(({ id }) => id));
                  }}
                  isMulti
                  isClearable
                  isDisabled={
                    !trackablesEnabled || !selectedTrackableLists.length
                  }
                  isLoading={
                    trackableNamesQuery.isFetching &&
                    !selectedTrackableItems.length
                  }
                />
              );
            }}
          />
        </Field>
      </div>

      <Field label="Confidence">
        <Controller
          name="confidence"
          control={control}
          render={({ field: { onChange, value } }) => (
            <OptionalTooltip
              showTooltip={isConfidenceSliderDisabled}
              content="Enable scenes search to set confidence"
            >
              <FilesSearchConfidenceSlider
                value={value}
                onChangeEnd={onChange}
                disabled={isConfidenceSliderDisabled}
              />
            </OptionalTooltip>
          )}
        />
      </Field>

      <Field label="Search by">
        <Controller
          name="search_entities"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FilesSearchEntitiesInput
              id="files-search-entities-input-widget"
              value={value}
              onChange={onChange}
            />
          )}
        />
      </Field>

      <Field label="Sort by">
        <Controller
          name="sort_by"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Radio id="search_sort" value={value} onChange={onChange}>
              <RadioOption value="relevance">Relevance</RadioOption>
              <RadioOption value="created_at">Date</RadioOption>
            </Radio>
          )}
        />
      </Field>

      <Accordion
        variant="flat"
        label="Additional filters"
        defaultOpen={
          options.camera_ids.length > 0 ||
          options.stream_ids.length > 0 ||
          options.gateway_ids.length > 0 ||
          Boolean(options.trackable_list_ids?.length) ||
          Boolean(options.trackable_item_ids?.length)
        }
      >
        <Field label="Cameras">
          <Controller
            name="camera_ids"
            control={control}
            render={({ field: { onChange } }) => {
              if (!hasDefaultCameras) {
                return (
                  <CameraSelect
                    size="small"
                    menuPlacement="top"
                    onChange={(cameras) =>
                      onChange(cameras.map(({ id }) => id))
                    }
                    isMulti
                  />
                );
              }

              if (isCamerasLoading || !cameras) {
                return <Skeleton height="2rem" width="100%" />;
              }

              return (
                <CameraSelect
                  size="small"
                  menuPlacement="top"
                  defaultValue={cameras.data}
                  onChange={(cameras) => onChange(cameras.map(({ id }) => id))}
                  isMulti
                />
              );
            }}
          />
        </Field>

        <Field label="Streams">
          <Controller
            name="stream_ids"
            control={control}
            render={({ field: { onChange } }) => {
              if (!hasDefaultStreams) {
                return (
                  <StreamSelect
                    size="small"
                    menuPlacement="top"
                    onChange={(streams) =>
                      onChange(streams.map(({ id }) => id))
                    }
                    queryFilters={{
                      sources: ['camera_stream', 'uri_stream'],
                      stream_types: ['file', 'rtsp'],
                    }}
                    isMulti
                  />
                );
              }

              if (isStreamsLoading || !streams) {
                return <Skeleton height="2rem" width="100%" />;
              }

              return (
                <StreamSelect
                  size="small"
                  defaultValue={streams.data}
                  menuPlacement="top"
                  onChange={(streams) => onChange(streams.map(({ id }) => id))}
                  queryFilters={{
                    sources: ['camera_stream', 'uri_stream'],
                    stream_types: ['file', 'rtsp'],
                  }}
                  isMulti
                />
              );
            }}
          />
        </Field>

        <Field label="Gateways">
          <Controller
            name="gateway_ids"
            control={control}
            render={({ field: { onChange } }) => {
              if (!hasDefaultGateways) {
                return (
                  <GatewaySelect
                    size="small"
                    menuPlacement="top"
                    onChange={(gateways) =>
                      onChange(gateways.map(({ id }) => id))
                    }
                    isMulti
                  />
                );
              }

              if (isGatewaysLoading || !gateways) {
                return <Skeleton height="2rem" width="100%" />;
              }

              return (
                <GatewaySelect
                  size="small"
                  menuPlacement="top"
                  defaultValue={gateways.data}
                  onChange={(gateways) =>
                    onChange(gateways.map(({ id }) => id))
                  }
                  isMulti
                />
              );
            }}
          />
        </Field>
      </Accordion>

      <WidgetFormFooter isDirty={isDirty} isLoading={isUpdating} />
    </form>
  );
}
